import { Info, MapPin, Truck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatEUR } from "@/lib/format";

function buildPickupLabel(lot) {
  const country = lot.pickup_country || lot.seller_country;
  const city = lot.pickup_city;
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return "Paese venditore n/d";
}

function PickupRow({ lot, flagUrl }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {flagUrl ? (
        <img src={flagUrl} alt="" loading="lazy" className="w-4 h-3 object-cover flex-shrink-0" />
      ) : (
        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      <span className="truncate" data-testid={`lot-country-${lot.id}`}>
        {buildPickupLabel(lot)}
      </span>
    </div>
  );
}

function PickupOnlyBadge({ lot }) {
  // Solo ritiro (no shipping at all)
  return (
    <span
      className="flex items-center gap-1 text-gold uppercase tracking-wider text-[10px]"
      data-testid={`lot-pickup-only-${lot.id}`}
    >
      <MapPin className="w-3 h-3" /> Solo ritiro
    </span>
  );
}

function ShippingCost({ lot }) {
  const days = lot.shipping_days_from && lot.shipping_days_to
    ? ` · ${lot.shipping_days_from}-${lot.shipping_days_to}gg`
    : "";
  return (
    <span
      className="flex items-center gap-1 text-white text-[11px] font-medium"
      data-testid={`lot-shipping-cost-${lot.id}`}
    >
      <Truck className="w-3 h-3 text-gold" />
      Costo spedizione {formatEUR(lot.shipping_eur)}{days}
    </span>
  );
}

function ShippingUnavailable({ lot }) {
  return (
    <span
      className="flex items-center gap-1 text-red-400/80 text-[11px]"
      data-testid={`lot-shipping-unavailable-${lot.id}`}
    >
      <Truck className="w-3 h-3" />
      Sped. non disp. in IT
    </span>
  );
}

function FreeShipping() {
  return (
    <span className="flex items-center gap-1 text-gold uppercase tracking-wider text-[10px]">
      <Truck className="w-3 h-3" /> Sped. gratis
    </span>
  );
}

function LoginNeeded({ lot }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="flex items-center gap-1 text-white/40 hover:text-white/80 transition-colors"
          data-testid={`lot-shipping-info-${lot.id}`}
        >
          <Truck className="w-3 h-3" />
          Sped. a pagamento
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs bg-surface border-white/10 text-white/85 text-xs"
      >
        Configura il cookie di sessione Catawiki (in alto a destra) per vedere il costo esatto in EUR.
      </TooltipContent>
    </Tooltip>
  );
}

function pickShippingBadge(lot) {
  if (lot.pickup_only) return <PickupOnlyBadge lot={lot} />;
  if (typeof lot.shipping_eur === "number") return <ShippingCost lot={lot} />;
  if (lot.shipping_unavailable) return <ShippingUnavailable lot={lot} />;
  if (lot.has_free_shipping) return <FreeShipping />;
  return <LoginNeeded lot={lot} />;
}

export default function ShippingInfo({ lot }) {
  const countryCode = lot.seller_country_code || lot.pickup_country_code;
  const flagUrl = countryCode
    ? `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`
    : null;

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="mt-4 pt-4 border-t border-white/8 flex flex-col gap-2 text-xs text-white/55"
        data-testid={`lot-shipping-${lot.id}`}
      >
        <PickupRow lot={lot} flagUrl={flagUrl} />
        <div className="flex items-center gap-2">
          {pickShippingBadge(lot)}
        </div>
      </div>
    </TooltipProvider>
  );
}
